import { DateTime } from "luxon";

const daysOfWeek = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

export function convertTimeRangesToUTC(
  dayOrDate,
  timeRanges,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
) {
  const isDate = !isNaN(Date.parse(dayOrDate));
  const now = DateTime.now().setZone(timezone);
  const referenceSunday = now.startOf("week").minus({ days: 1 });

  const baseDate = isDate
    ? DateTime.fromISO(dayOrDate, { zone: timezone })
    : referenceSunday.plus({ days: getDayOffset(dayOrDate) });

  let groupedResults = {};

  timeRanges.forEach(({ start, end }) => {
    const startDateTime = baseDate.set({
      hour: parseInt(start.split(":")[0], 10),
      minute: parseInt(start.split(":")[1], 10),
    });

    const endDateTime = baseDate.set({
      hour: parseInt(end.split(":")[0], 10),
      minute: parseInt(end.split(":")[1], 10),
    });

    const startUTC = startDateTime.toUTC();
    const endUTC = endDateTime.toUTC();

    const addToGroupedResults = (day, date, start, end) => {
      if (!groupedResults[day]) {
        groupedResults[day] = [];
      }
      groupedResults[day].push({ date, start, end });
    };

    if (startUTC.day !== endUTC.day) {
      addToGroupedResults(
        startUTC.toFormat("EEEE").toLowerCase(),
        startUTC.toISODate(),
        startUTC.toFormat("HH:mm"),
        "24:00"
      );
      addToGroupedResults(
        endUTC.toFormat("EEEE").toLowerCase(),
        endUTC.toISODate(),
        "00:00",
        endUTC.toFormat("HH:mm")
      );
    } else {
      addToGroupedResults(
        startUTC.toFormat("EEEE").toLowerCase(),
        startUTC.toISODate(),
        startUTC.toFormat("HH:mm"),
        endUTC.toFormat("HH:mm")
      );
    }
  });

  return groupedResults;
}

/**
 * Converts multiple UTC time ranges to local timezone, merges overlapping slots, and groups by day.
 * @param {Object} utcTimeRanges - UTC time ranges structured by days.
 * @param {string} timezone - User's local timezone (e.g., "Asia/Kolkata").
 * @returns {Object} - Converted and merged time ranges grouped by local day.
 */
export function convertUTCRangesToLocal(utcTimeRanges, timezone) {
  const now = DateTime.utc();
  const referenceSunday = now.startOf("week").minus({ days: 1 }); // Previous Sunday in UTC
  let localTimeRanges = {};

  for (const [utcDay, ranges] of Object.entries(utcTimeRanges)) {
    if (!Array.isArray(ranges) || ranges.length === 0) continue; // Skip if no ranges exist

    const baseDate = ranges[0].date
      ? DateTime.fromISO(ranges[0].date, { zone: "UTC" }) // Use provided date
      : referenceSunday.plus({
          days: daysOfWeek.indexOf(utcDay.toLowerCase()),
        });

    for (const { start, end } of ranges) {
      const startUTC = baseDate.set({
        hour: parseInt(start.split(":")[0], 10),
        minute: parseInt(start.split(":")[1], 10),
      });

      const endUTC = baseDate.set({
        hour: parseInt(end.split(":")[0], 10),
        minute: parseInt(end.split(":")[1], 10),
      });

      const startLocal = startUTC.setZone(timezone);
      const endLocal = endUTC.setZone(timezone);

      if (startLocal.day !== endLocal.day) {
        const firstDay = startLocal.toFormat("EEEE").toLowerCase();
        const secondDay = endLocal.toFormat("EEEE").toLowerCase();

        if (!localTimeRanges[firstDay]) localTimeRanges[firstDay] = [];
        if (!localTimeRanges[secondDay]) localTimeRanges[secondDay] = [];

        localTimeRanges[firstDay].push({
          start: startLocal.toFormat("HH:mm"),
          end: "24:00",
        });

        localTimeRanges[secondDay].push({
          start: "00:00",
          end: endLocal.toFormat("HH:mm"),
        });
      } else {
        const localDay = startLocal.toFormat("EEEE").toLowerCase();
        if (!localTimeRanges[localDay]) localTimeRanges[localDay] = [];

        localTimeRanges[localDay].push({
          start: startLocal.toFormat("HH:mm"),
          end: endLocal.toFormat("HH:mm"),
        });
      }
    }

    if (localTimeRanges && Object.keys(localTimeRanges).length > 0) {
      for (const day of Object.keys(localTimeRanges)) {
        if (!localTimeRanges[day] || localTimeRanges[day].length === 0) continue; // Skip empty days

        localTimeRanges[day].sort((a, b) => a.start.localeCompare(b.start));

        let mergedRanges = [];
        let prev = localTimeRanges[day][0];

        for (let i = 1; i < localTimeRanges[day].length; i++) {
          let current = localTimeRanges[day][i];
          if (prev.end >= current.start) {
            prev.end = current.end;
          } else {
            mergedRanges.push(prev);
            prev = current;
          }
        }
        mergedRanges.push(prev);

        localTimeRanges[day] = mergedRanges.filter(
          (range) => !(range.start === "00:00" && range.end === "00:00")
        );
      }
    }
  }

  return localTimeRanges;
}

export function convertUTCDatesToLocal(
  utcData,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
) {
  let localDateMap = {};

  for (const key in utcData) {
    if (!utcData[key] || !utcData[key].date || !Array.isArray(utcData[key].timeslots)) {
      continue; // Skip invalid entries
    }

    const dateUTC = DateTime.fromISO(utcData[key].date, { zone: "UTC" });

    for (const { start, end } of utcData[key].timeslots) {
      let startUTC = dateUTC.set({
        hour: parseInt(start.split(":")[0], 10),
        minute: parseInt(start.split(":")[1], 10),
      });

      let endUTC = dateUTC.set({
        hour: parseInt(end.split(":")[0], 10),
        minute: parseInt(end.split(":")[1], 10),
      });

      let startLocal = startUTC.setZone(timezone);
      let endLocal = endUTC.setZone(timezone);

      // If the time range crosses midnight, split it into two dates
      if (startLocal.day !== endLocal.day) {
        let firstDate = startLocal.toISODate();
        let secondDate = endLocal.toISODate();

        if (!localDateMap[firstDate]) localDateMap[firstDate] = [];
        if (!localDateMap[secondDate]) localDateMap[secondDate] = [];

        localDateMap[firstDate].push({ start: startLocal.toFormat("HH:mm"), end: "24:00" });
        localDateMap[secondDate].push({ start: "00:00", end: endLocal.toFormat("HH:mm") });
      } else {
        let localDate = startLocal.toISODate();
        if (!localDateMap[localDate]) localDateMap[localDate] = [];

        localDateMap[localDate].push({
          start: startLocal.toFormat("HH:mm"),
          end: endLocal.toFormat("HH:mm"),
        });
      }
    }
  }

  // Merge overlapping or consecutive slots
  for (const date in localDateMap) {
    localDateMap[date].sort((a, b) => a.start.localeCompare(b.start)); // Sort by start time

    let mergedRanges = [];
    let prev = localDateMap[date][0];

    for (let i = 1; i < localDateMap[date].length; i++) {
      let current = localDateMap[date][i];

      if (prev.end >= current.start) {
        prev.end = current.end; // Merge overlapping/consecutive slots
      } else {
        mergedRanges.push(prev);
        prev = current;
      }
    }
    mergedRanges.push(prev);

    // Remove dates where all slots are "00:00-00:00"
    mergedRanges = mergedRanges.filter(
      (range) => !(range.start === "00:00" && range.end === "00:00")
    );

    if (mergedRanges.length > 0) {
      localDateMap[date] = mergedRanges;
    } else {
      delete localDateMap[date]; // Remove date if all slots were "00:00-00:00"
    }
  }

  return localDateMap;
}

function getDayOffset(day) {
  const days = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  return days.indexOf(day.toLowerCase());
}

export function transformTimeSlots(timeSlots) {
  return Object.entries(timeSlots).map(([day, timeslots]) => ({
    days: [day],
    timeslots,
  }));
}
export function transformTimeSlotsByDate(timeSlots) {
  return Object.entries(timeSlots).map(([date, slots]) => ({
    dates: [date],
    timeslots: slots,
  }));
}

export const getSlotDifferences = (updatedSlots, previousSlots = []) => {
  function generateHalfHourSlots(slots) {
    const timeSlots = [];
    slots.forEach(({ start, end }) => {
      let [startHour, startMin] = start.split(":").map(Number);
      let [endHour, endMin] = end.split(":").map(Number);

      while (startHour < endHour || (startHour === endHour && startMin < endMin)) {
        let nextMin = startMin + 30;
        let nextHour = startHour;
        if (nextMin === 60) {
          nextMin = 0;
          nextHour += 1;
        }
        let slotStart = `${String(startHour).padStart(2, "0")}:${String(startMin).padStart(
          2,
          "0"
        )}`;
        let slotEnd = `${String(nextHour).padStart(2, "0")}:${String(nextMin).padStart(2, "0")}`;
        timeSlots.push({ start: slotStart, end: slotEnd });

        startHour = nextHour;
        startMin = nextMin;
      }
    });
    return timeSlots;
  }

  function mergeSlots(slots) {
    if (slots.length === 0) return [];
    slots.sort((a, b) => a.start.localeCompare(b.start));

    let merged = [slots[0]];
    for (let i = 1; i < slots.length; i++) {
      let lastSlot = merged[merged.length - 1];
      if (lastSlot.end === slots[i].start) {
        lastSlot.end = slots[i].end;
      } else {
        merged.push(slots[i]);
      }
    }
    return merged;
  }

  const updatedSet = new Set(generateHalfHourSlots(updatedSlots).map((s) => `${s.start}-${s.end}`));
  const previousSet = new Set(
    generateHalfHourSlots(previousSlots).map((s) => `${s.start}-${s.end}`)
  );

  const deletedSlots = [...previousSet]
    .filter((slot) => !updatedSet.has(slot))
    .map((slot) => {
      let [start, end] = slot.split("-");
      return { start, end };
    });

  const newSlots = [...updatedSet]
    .filter((slot) => !previousSet.has(slot))
    .map((slot) => {
      let [start, end] = slot.split("-");
      return { start, end };
    });

  return {
    deletedSlots: mergeSlots(deletedSlots),
    newSlots: mergeSlots(newSlots),
  };
};
export const mergeTimeSlots = (data) => {
  const groupedSlots = {};

  data.forEach(({ days, timeslots }) => {
    const day = days[0]; // Assuming a single day per entry
    if (!groupedSlots[day]) {
      groupedSlots[day] = [];
    }
    groupedSlots[day] = groupedSlots[day].concat(timeslots);
  });

  const mergedData = Object.keys(groupedSlots).map((day) => {
    let timeslots = groupedSlots[day];

    timeslots.sort((a, b) => a.start.localeCompare(b.start));

    const mergedTimeslots = [];
    let prevSlot = timeslots[0];

    for (let i = 1; i < timeslots.length; i++) {
      const currentSlot = timeslots[i];

      // If slots are continuous or overlapping, merge them
      if (prevSlot.end >= currentSlot.start) {
        prevSlot.end = currentSlot.end;
      } else {
        mergedTimeslots.push(prevSlot);
        prevSlot = currentSlot;
      }
    }
    mergedTimeslots.push(prevSlot);

    return { days: [day], timeslots: mergedTimeslots };
  });

  return mergedData;
};
